import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import {
  AppShell,
  BrandMark,
  endSession,
  SIGN_OUT_FAILED_MESSAGE,
  Icon,
  cn,
  containerClass,
  navLinkClass,
  type NavVariant,
} from '@dataroom/shared';
import { useMyTeam, useSession } from '@/api/hooks';
import { AccessEndsLine } from '@/components/AccessEndsLine';
import { buyerRooms } from '@/config';
import { contractsEnabled } from '@/lib/features';

/**
 * The buyer portal chrome — the shared AppShell (one header and 1200px
 * container for all three apps; nav collapses to a drawer under 768px) with
 * "Your rooms" and Sign out. Used by the rooms landing and the room view.
 */
export function PortalFrame({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  function signOut() {
    void endSession(qc).then((ended) => {
      if (ended) navigate('/login');
      else window.alert(SIGN_OUT_FAILED_MESSAGE);
    });
  }

  return (
    <AppShell
      brand={
        <Link to="/" aria-label="ClosingVault Buyer — your rooms" className="rounded-md">
          <BrandMark appLabel="Buyer" />
        </Link>
      }
      renderNav={(variant: NavVariant, close) => (
        <NavLink
          to="/"
          end
          onClick={close}
          className={({ isActive }) => navLinkClass(variant, isActive)}
        >
          Your rooms
        </NavLink>
      )}
      renderActions={(variant: NavVariant, close) => (
        <>
          <NavLink
            to="/help"
            onClick={close}
            className={({ isActive }) => navLinkClass(variant, isActive)}
          >
            <Icon name="help" />
            Help
          </NavLink>
          <button
            type="button"
            className={navLinkClass(variant, false)}
            onClick={() => {
              close();
              signOut();
            }}
          >
            Sign out
          </button>
        </>
      )}
    >
      {children}
    </AppShell>
  );
}

/**
 * Chrome for a room-scoped buyer view (mounted at /rooms/:roomId): the room
 * header — a "Your rooms" back link and the room's name as the page's h1 — so a
 * buyer in more than one deal is never stranded inside one room.
 */
export function Layout() {
  const { roomId } = useParams<{ roomId: string }>();
  const session = useSession();
  const room = buyerRooms(session.data).find((r) => r.roomId === roomId);
  const roomName = room?.name;
  // "My team" is offered wherever the buyer has a team page (not a buyer-run room).
  const team = useMyTeam(roomId ?? '');

  return (
    <PortalFrame>
      <div className={cn(containerClass, 'flex flex-col gap-2 pt-6')}>
        <Link
          to="/"
          className="inline-flex w-fit items-center gap-1.5 text-[13px] text-ink-2 hover:underline"
        >
          <Icon name="arrowLeft" size={14} />
          Your rooms
        </Link>
        <h1 className="break-words font-serif text-2xl font-semibold leading-8 tracking-tight text-balance md:text-[28px] md:leading-9">
          {roomName ?? 'Your data room'}
        </h1>
        <AccessEndsLine accessEndsAt={room?.accessEndsAt} />
        {roomId ? (
          <nav aria-label="Room sections" className="flex gap-4 text-sm">
            <NavLink
              to={`/rooms/${roomId}`}
              end
              className={({ isActive }) =>
                cn('hover:underline', isActive ? 'font-semibold text-ink' : 'text-ink-2')
              }
            >
              Data room
            </NavLink>
            {contractsEnabled() ? (
              <NavLink
                to={`/rooms/${roomId}/contracts`}
                className={({ isActive }) =>
                  cn('hover:underline', isActive ? 'font-semibold text-ink' : 'text-ink-2')
                }
              >
                Contracts
              </NavLink>
            ) : null}
            {team.isSuccess ? (
              <NavLink
                to={`/rooms/${roomId}/team`}
                className={({ isActive }) =>
                  cn('hover:underline', isActive ? 'font-semibold text-ink' : 'text-ink-2')
                }
              >
                My team
              </NavLink>
            ) : null}
          </nav>
        ) : null}
      </div>
      <main>
        <Outlet />
      </main>
    </PortalFrame>
  );
}
